import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import api from "../lib/api";
import { simulate } from "../lib/loanMath";
import { formatCLP, apiError } from "../lib/format";
import { toast } from "sonner";
import {
  Calculator,
  X,
  PaperPlaneTilt,
  HandCoins,
  CalendarBlank,
  Percent,
  TrendUp,
} from "@phosphor-icons/react";

/**
 * Loan simulator rendered inside the advisor chat header.
 * Generates a PNG snapshot of the plan and posts it as a chat message attachment.
 *
 * Props:
 *   open: boolean
 *   onClose()
 *   clientName: string (for filename/context)
 *   conversationId: string
 *   onSent(message): callback after the chat message is posted
 */
export default function ChatSimulator({
  open,
  onClose,
  clientName = "Cliente",
  conversationId,
  onSent,
}) {
  const [amount, setAmount] = useState(1000000);
  const [cuotas, setCuotas] = useState(12);
  const [rate, setRate] = useState(1.2);
  const [sending, setSending] = useState(false);
  const snapshotRef = useRef(null);

  const sim = useMemo(() => simulate(amount, rate, cuotas), [amount, rate, cuotas]);

  const caePct = useMemo(() => {
    const r = Number(rate) / 100;
    if (!isFinite(r) || r <= 0) return 0;
    return (Math.pow(1 + r, 12) - 1) * 100;
  }, [rate]);

  const sendAsImage = async () => {
    if (!snapshotRef.current) return;
    if (amount < 500000) {
      toast.error("Monto mínimo $ 500.000");
      return;
    }
    if (cuotas < 3 || cuotas > 220) {
      toast.error("Cuotas entre 3 y 220");
      return;
    }
    setSending(true);
    try {
      // 1) Capture the summary card as a PNG
      const canvas = await html2canvas(snapshotRef.current, {
        backgroundColor: "#0E4D28",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png", 0.92)
      );
      if (!blob) throw new Error("No se pudo generar la imagen");

      // 2) Upload it through /uploads
      const safeName = clientName.replace(/\s+/g, "_").toLowerCase();
      const file = new File([blob], `simulacion_${safeName}.png`, { type: "image/png" });
      const form = new FormData();
      form.append("file", file);
      const { data: att } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // 3) Post the chat message with a human-readable caption
      const caption = [
        `Simulación de crédito`,
        `• Monto: ${formatCLP(sim.principal)}`,
        `• Cuotas: ${sim.cuotas} meses`,
        `• Tasa mensual: ${sim.ratePct.toFixed(2)}%`,
        `• Cuota estimada: ${formatCLP(Math.round(sim.monthly))}`,
        `• Total a pagar: ${formatCLP(Math.round(sim.total))}`,
      ].join("\n");

      const { data: msg } = await api.post(
        `/chat/conversations/${conversationId}/messages`,
        { text: caption, attachment_id: att.id }
      );

      toast.success("Simulación enviada al cliente");
      onSent?.(msg);
      onClose?.();
    } catch (e) {
      toast.error(apiError(e, "No se pudo enviar la simulación"));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="chat-simulator-modal"
    >
      <div
        className="bg-surface max-w-3xl w-full max-h-[92vh] overflow-auto border border-hairline shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-hairline flex items-center justify-between sticky top-0 bg-surface z-10">
          <div className="flex items-center gap-2">
            <Calculator size={20} weight="duotone" className="text-forest" />
            <div>
              <div className="label-mono">Simulador rápido</div>
              <div className="font-display font-bold text-lg">
                Cotización para <span className="text-forest">{clientName}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg"
            data-testid="chat-simulator-close"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Controls */}
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-mono flex items-center gap-1">
                  <HandCoins size={12} weight="duotone" /> Monto · mín $500K
                </label>
                <span className="font-mono text-sm text-burnt">{formatCLP(amount)}</span>
              </div>
              <input
                type="number"
                min="500000"
                step="50000"
                value={amount}
                onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
                className="w-full h-12 border border-hairline bg-bg px-3 font-mono text-lg focus:outline-none focus:border-forest"
                data-testid="chatsim-amount"
              />
              <input
                type="range"
                min="500000"
                max="50000000"
                step="50000"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full mt-2 accent-forest"
                data-testid="chatsim-amount-range"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-mono flex items-center gap-1">
                    <CalendarBlank size={12} weight="duotone" /> Cuotas · 3–220
                  </label>
                  <span className="font-mono text-sm">{cuotas} meses</span>
                </div>
                <input
                  type="number"
                  min="3"
                  max="220"
                  value={cuotas}
                  onChange={(e) =>
                    setCuotas(Math.max(3, Math.min(220, Number(e.target.value) || 3)))
                  }
                  className="w-full h-11 border border-hairline bg-bg px-3 font-mono focus:outline-none focus:border-forest"
                  data-testid="chatsim-cuotas"
                />
                <input
                  type="range"
                  min="3"
                  max="220"
                  value={cuotas}
                  onChange={(e) => setCuotas(Number(e.target.value))}
                  className="w-full mt-2 accent-forest"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-mono flex items-center gap-1">
                    <Percent size={12} weight="duotone" /> Tasa mensual
                  </label>
                  <span className="font-mono text-sm">{Number(rate).toFixed(2)} %</span>
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.05"
                  value={rate}
                  onChange={(e) => setRate(Math.max(0, Number(e.target.value) || 0))}
                  className="w-full h-11 border border-hairline bg-bg px-3 font-mono focus:outline-none focus:border-forest"
                  data-testid="chatsim-rate"
                />
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="0.05"
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full mt-2 accent-forest"
                />
              </div>
            </div>
          </div>

          {/* Snapshot card (this is what gets captured as PNG) */}
          <div
            ref={snapshotRef}
            className="bg-forest text-bg p-6"
            data-testid="chatsim-snapshot"
          >
            <div className="flex items-start justify-between border-b border-bg/20 pb-4 mb-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-bg/60">
                  EFE · Simulación
                </div>
                <div className="font-display font-black text-2xl mt-1">
                  Plan de crédito
                </div>
                <div className="text-xs font-mono mt-1 text-bg/70">
                  Cliente: {clientName}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono uppercase tracking-widest text-bg/60">
                  Cuota mensual
                </div>
                <div className="font-mono text-3xl font-medium">
                  {formatCLP(Math.round(sim.monthly))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <Metric label="Monto" value={formatCLP(sim.principal)} />
              <Metric label="Plazo" value={`${sim.cuotas} meses`} />
              <Metric label="Tasa mensual" value={`${sim.ratePct.toFixed(2)} %`} />
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-bg/20">
              <Metric
                label="Total a pagar"
                value={formatCLP(Math.round(sim.total))}
              />
              <Metric
                label="Intereses"
                value={formatCLP(Math.round(sim.interest))}
                accent
              />
              <Metric label="CAE estimado" value={`${caePct.toFixed(2)} %`} />
            </div>

            <div className="mt-5 pt-4 border-t border-bg/20 text-[11px] font-mono text-bg/70 flex items-center gap-2">
              <TrendUp size={12} weight="duotone" /> Sistema francés · Cuota fija ·
              Simulación referencial
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="h-11 px-4 border border-hairline text-sm"
              data-testid="chatsim-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={sendAsImage}
              disabled={sending}
              className="h-11 px-5 bg-burnt text-bg text-sm font-semibold flex items-center gap-2 hover:bg-burnt-hover disabled:opacity-50"
              data-testid="chatsim-send"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {sending ? "Enviando…" : "Enviar como imagen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-bg/60">
        {label}
      </div>
      <div
        className={`font-mono mt-1 ${accent ? "text-leaf" : ""} text-base font-medium`}
      >
        {value}
      </div>
    </div>
  );
}
